import { useCallback, useEffect, useRef } from 'react';

import { useFocusRestoration } from './use-focus-restoration';

const FOCUS_RESTORE_DELAY_MS = 200;

interface ModalFocusConfig {
  modalName: string;
  isModalOpen: (modalName: string) => boolean;
  shouldRestoreFocus?: () => boolean;
  fallbackElementRef?: React.RefObject<HTMLElement>;
  onBeforeOpen?: () => void;
  onAfterFocusRestore?: () => void;
}

/**
 * Generic hook for managing focus restoration when modals close.
 *
 * This hook provides a reusable solution for ensuring that when a modal closes,
 * focus returns to the element that opened it, even when multiple instances of
 * the same component can open the same modal.
 *
 * @param config Configuration object for modal focus management
 * @returns Object with handlers and utilities for modal focus management
 */
export const useModalFocusManagement = (
  config: ModalFocusConfig
): {
  handleOpenModal: (openModalCallback: () => void, customBeforeOpen?: () => void) => void;
  manualRestoreFocus: () => void;
  isResponsibleForModal: () => boolean;
  isModalOpen: boolean;
  focusUtils: {
    storeFocus: () => void;
    restoreFocus: () => void;
    clearStoredFocus: () => void;
  };
} => {
  const {
    modalName,
    isModalOpen,
    shouldRestoreFocus,
    fallbackElementRef,
    onBeforeOpen,
    onAfterFocusRestore,
  } = config;

  const { storeFocus, restoreFocus, clearStoredFocus } = useFocusRestoration();
  const previousModalState = useRef<boolean>(false);
  const didOpenModal = useRef<boolean>(false);

  const modalIsOpen = isModalOpen(modalName);

  const handleFallbackFocus = useCallback(() => {
    if (fallbackElementRef?.current && document.contains(fallbackElementRef.current)) {
      fallbackElementRef.current.focus();
    }
  }, [fallbackElementRef]);

  const performFocusRestoration = useCallback(() => {
    requestAnimationFrame(() => {
      restoreFocus();

      // Fallback check after a short delay
      setTimeout(() => {
        if (document.activeElement === document.body || !document.activeElement) {
          handleFallbackFocus();
        }

        onAfterFocusRestore?.();
      }, FOCUS_RESTORE_DELAY_MS);
    });
  }, [restoreFocus, handleFallbackFocus, onAfterFocusRestore]);

  // Watch for modal state changes and handle focus restoration
  useEffect(() => {
    const wasOpen = previousModalState.current;
    const isCurrentlyOpen = modalIsOpen;

    // Only trigger focus restoration when modal transitions from open to closed
    if (wasOpen && !isCurrentlyOpen) {
      const shouldRestore = shouldRestoreFocus ? shouldRestoreFocus() : didOpenModal.current;

      if (shouldRestore) {
        // Reset the flag since we're handling the close
        didOpenModal.current = false;
        performFocusRestoration();
      }
    }

    // Update the previous state
    previousModalState.current = isCurrentlyOpen;
  }, [modalIsOpen, shouldRestoreFocus, performFocusRestoration]);

  /**
   * Cleanup stored focus on component unmount
   */
  useEffect(() => {
    return () => {
      clearStoredFocus();
    };
  }, [clearStoredFocus]);

  const handleOpenModal = useCallback(
    (openModalCallback: () => void, customBeforeOpen?: () => void) => {
      didOpenModal.current = true;
      storeFocus();
      onBeforeOpen?.();
      customBeforeOpen?.();
      openModalCallback();
    },
    [storeFocus, onBeforeOpen]
  );

  const manualRestoreFocus = useCallback(() => {
    performFocusRestoration();
  }, [performFocusRestoration]);

  const isResponsibleForModal = useCallback(() => {
    return shouldRestoreFocus ? shouldRestoreFocus() : didOpenModal.current;
  }, [shouldRestoreFocus]);

  return {
    handleOpenModal,
    manualRestoreFocus,
    isResponsibleForModal,
    isModalOpen: modalIsOpen,
    focusUtils: {
      storeFocus,
      restoreFocus,
      clearStoredFocus,
    },
  };
};
